"use client"

import { useReportWebVitals } from "next/web-vitals"
import { useState, type ReactNode } from "react"

import { enqueueRumVital } from "~/lib/analytics/rum-drain"
import { CORE_WEB_VITALS, type RumMetricName } from "~/lib/rum/types"

/**
 * Reports Core Web Vitals (+ the FCP/TTFB diagnostics) to the first-party RUM
 * drain. This is the Web-Vitals half of the "drain for Vercel analytics": the
 * same field measurements Speed Insights collects into a closed dashboard,
 * captured into our own warehouse so `rum:analyze` can ladder a slow route into
 * a `poor_web_vitals` ledger finding.
 *
 * Off unless `NEXT_PUBLIC_RUM_ENABLED === 'true'` and consent is granted (both
 * enforced inside `enqueueRumVital`), so mounting it is always safe. Renders
 * nothing; `useReportWebVitals` only attaches the browser's PerformanceObserver.
 */
const STORED_METRICS = new Set<RumMetricName>([
  ...CORE_WEB_VITALS,
  "FCP",
  "TTFB",
])

function isStoredMetric(name: string): name is RumMetricName {
  return STORED_METRICS.has(name as RumMetricName)
}

/**
 * DELIBERATE SEMANTICS — `device` here is the VIEWPORT class at report time
 * (`innerWidth < 768` = mobile), intentionally NOT a UA-derived device class
 * (see docs/analytics/rum-drain.md §Storage). Viewport is the
 * actionable signal for layout-driven vitals (CLS/LCP fail at layouts, which
 * are breakpoint-driven), and it needs no UA sniffing. Consequences accepted:
 * a desktop user in a narrow window records as `mobile`, and a mid-session
 * resize can split one visit across both keys — both are the layout-truthful
 * reading. Note the same word carries UA semantics in GSC's rows of the shared
 * ledger; do not "fix" this to UA sniffing.
 */
function deviceClass(): "mobile" | "desktop" {
  if (typeof window === "undefined") return "desktop"
  return window.innerWidth < 768 ? "mobile" : "desktop"
}

/** The subset of `next/web-vitals`'s metric we read. Annotated explicitly
 *  because the library types the callback arg loosely (it also delivers
 *  Next.js-specific timings), which the type-aware lint flags as unsafe. */
interface ReportedMetric {
  name: string
  value: number
  rating?: string
  navigationType?: string
}

export function RumReporter(): ReactNode {
  // Attribute every vital to the HARD page load that produced it. web-vitals
  // reports each metric once per page load, but CLS / INP / LCP often
  // FINALIZE late (visibility change, post-interaction) — potentially after a
  // client-side navigation — so reading `location.pathname` at CALLBACK time
  // would attribute the metric to the destination route and open
  // `poor_web_vitals` findings against pages that never produced it
  // (r3541077080). Captured once at mount: the reporter lives in the root
  // layout, mounting exactly once per hard load, which is the page every
  // metric this hook delivers belongs to.
  const [pageLoadPath] = useState<string>(() =>
    typeof window !== "undefined" ? window.location.pathname : "/",
  )

  useReportWebVitals((metric: ReportedMetric) => {
    // `useReportWebVitals` also emits Next.js-specific timings (hydration,
    // render) — forward only the standard Web Vitals the drain understands.
    if (!isStoredMetric(metric.name)) return
    enqueueRumVital({
      path: pageLoadPath,
      metric: metric.name,
      value: metric.value,
      rating: metric.rating,
      device: deviceClass(),
      navigationType: metric.navigationType,
    })
  })

  return null
}

export default RumReporter
